/**
 * useLstRibbon — Ribbon do módulo Rotinas de Liderança.
 *
 * Padrão Command Center:
 *  - Arquivo (primeiro) + sub-abas de navegação no slot beforeGroups.
 *  - Consciência Situacional no grupo Análise.
 *  - Sem aba Exibir (opções movidas para Arquivo).
 */

import React, { useMemo } from 'react';
import { BarChart3 } from 'lucide-react';
import type { RibbonConfig, RibbonGroup } from '../../../types/ribbon';
import { LstScreenTabs, type LstScreen } from '../components/LstScreenTabs';
import { LstRibbonArquivoSlot } from '../components/LstRibbonArquivoSlot';
import { LstRibbonPeriodSlot } from '../components/LstRibbonContextBar';
import type { LstPeriodView } from '../components/PeriodSwitcher';

export interface UseLstRibbonDeps {
  screen: LstScreen;
  onScreenChange: (screen: LstScreen) => void;
  canAudit: boolean;
  canConfigure: boolean;

  onSalvarPadrao: () => void;
  onSalvarComo: () => void;
  onExportPdf: () => void;
  onExportExcel: () => void;

  compact: boolean;
  onToggleCompact: () => void;
  hideDescriptions: boolean;
  onToggleHideDescriptions: () => void;

  onOpenKpi: () => void;

  showPeriod: boolean;
  periodView: LstPeriodView;
  onPeriodChange: (v: LstPeriodView) => void;
}

export function useLstRibbon(deps: UseLstRibbonDeps): RibbonConfig {
  const {
    screen,
    onScreenChange,
    canAudit,
    canConfigure,
    onSalvarPadrao,
    onSalvarComo,
    onExportPdf,
    onExportExcel,
    compact,
    onToggleCompact,
    hideDescriptions,
    onToggleHideDescriptions,
    onOpenKpi,
    showPeriod,
    periodView,
    onPeriodChange,
  } = deps;

  return useMemo<RibbonConfig>(() => {
    const groups: RibbonGroup[] = [
      {
        id: 'analise',
        label: 'Análise',
        position: 'left',
        actions: [
          {
            id: 'analise-kpi',
            label: 'Consciência Situacional',
            icon: BarChart3,
            onClick: onOpenKpi,
            tooltip: 'Turno atual · atalho para Diagnóstico',
          },
        ],
      },
    ];

    return {
      moduleId: 'operations',
      groups,
      slots: {
        beforeGroups: (
          <div className="flex h-full shrink-0 items-center gap-0.5">
            <LstRibbonArquivoSlot
              onSalvarPadrao={onSalvarPadrao}
              onSalvarComo={onSalvarComo}
              onExportPdf={onExportPdf}
              onExportExcel={onExportExcel}
              compact={compact}
              onToggleCompact={onToggleCompact}
              hideDescriptions={hideDescriptions}
              onToggleHideDescriptions={onToggleHideDescriptions}
            />
            <LstScreenTabs
              screen={screen}
              onChange={onScreenChange}
              canAudit={canAudit}
              canConfigure={canConfigure}
            />
          </div>
        ),
        afterGroups: (
          <LstRibbonPeriodSlot
            showPeriod={showPeriod}
            periodView={periodView}
            onPeriodChange={onPeriodChange}
          />
        ),
      },
    };
  }, [
    screen,
    onScreenChange,
    canAudit,
    canConfigure,
    onSalvarPadrao,
    onSalvarComo,
    onExportPdf,
    onExportExcel,
    compact,
    onToggleCompact,
    hideDescriptions,
    onToggleHideDescriptions,
    onOpenKpi,
    showPeriod,
    periodView,
    onPeriodChange,
  ]);
}
